import { useChatStatus } from '@ai-sdk-tools/store';
import { useMutation } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { FileIcon, useDownloadFile } from '@newfront-insurance/admin-ui/files';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { graphql } from '@newfront-insurance/graphql-frontend';
import { useNotifications } from '@newfront-insurance/next-notifications';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ClipboardList } from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { ClipboardEvent, ReactNode } from 'react';
import { useCopyToClipboard } from 'react-use';

import {
  ChainOfThoughtBlock,
  CompletedChainOfThoughtBlock,
  GroupedChainOfThoughtBlock,
} from './chain-of-thought-block';
import { ChatContextUsage } from './chat-context-usage';
import { MarkdownWithCitations } from './markdown-with-citations';
import { GeneratedFilesList } from './tools/process-files-tool';
import { getClarificationPart, isClarificationPending, isClarificationToolPart } from '../../../lib/clarification';
import { containsSandboxLinks } from '../../../lib/preprocess-sandbox-links';
import { startsWithSkillCommand } from '../../../lib/skill-command-utils';
import type { MessageReaction, UIMessageWithMetadata } from '../../../lib/types';
import { sanitizeText } from '../../../lib/utils';
import { MessageResponse, Sources, SourcesContent, SourcesTrigger } from '../../ai-elements';
import { SourceBadge } from '../../ai-elements/source-badge';
import { ThumbsDown } from '../../icons/thumbs-down';
import { ThumbsUp } from '../../icons/thumbs-up';
import { MessageAction, MessageActions } from '../../message-actions';
import { useChatOverlayContextSafe } from '../contexts/chat-overlay-context';
import { useChainOfThoughtSteps } from '../hooks/use-chain-of-thought-steps';
import { getFileExtension, getLengthBucket, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../lib/analytics';
import {
  type CoTStepBlockGroupingMode,
  getCoTStepBlockRenderItems,
  getCoTStepBlocks,
  getCoTStepSegmentBeforePart,
  isFinalAnswerTextPart,
} from '../lib/chain-of-thought-utils';
import { type DataPartRenderer, type ToolOutputRenderer } from '../lib/default-renderers';
import {
  buildSandboxFileMap,
  extractAllSourcesFromMessage,
  extractSourcesFromMessage,
  getSourcesArray,
  hasSources,
  type SourcesMap,
} from '../lib/sources';

const ADD_MESSAGE_REACTION = graphql(`
  mutation AddMessageReaction($messageUuid: String!, $reaction: MessageReaction) {
    updateMessageReaction(messageUuid: $messageUuid, reaction: $reaction) {
      uuid
      reaction
    }
  }
`);

interface PreviewMessageProps {
  message: UIMessageWithMetadata;
  isStreaming: boolean;
  isLast: boolean;
  onReaction: (reaction: MessageReaction | null) => void;
  /**
   * Sources accumulated from messages before the current one.
   * Used to resolve follow-up citations when source-url parts are omitted.
   */
  sourceFallbackMap?: SourcesMap;
  /**
   * Object mapping tool names to their renderer functions.
   * If provided, these renderers will be used instead of default rendering.
   */
  toolRenderers?: Record<string, ToolOutputRenderer>;
  /**
   * Object mapping data part types to their renderer functions.
   * If provided, these renderers will be used instead of default rendering.
   */
  dataPartRenderers?: Record<string, DataPartRenderer>;
  /**
   * Optional renderer function for content to display inside the message bubble
   * (for user messages) before the text content. Receives the message as a parameter.
   */
  beforeContentRenderer?: (message: UIMessageWithMetadata) => ReactNode;
  /**
   * Optional className to apply to the wrapper around beforeContentRenderer output.
   * Useful for adjusting spacing or positioning of the before-content element.
   */
  beforeContentClassName?: string;
  /**
   * Optional className to apply to the message content container (the bubble).
   * Useful for adjusting padding or other styling of the message bubble.
   */
  messageContentClassName?: string;
  /**
   * Controls whether inline citations and sources panel are rendered.
   * When false, falls back to plain markdown rendering using Response component.
   * Defaults to true for backward compatibility.
   */
  enableSourcesRendering?: boolean;
}

/**
 * Renders text with the slash command handle highlighted in fire-400 color.
 * Extracts the handle from the beginning of the text (e.g., "/compare-policies").
 * Preserves whitespace formatting exactly as typed.
 */
function SlashCommandHighlightedText({ text }: { text: string }) {
  // Match slash command handle at the start: /word-characters
  const match = text.match(/^(\/[\w-]+)(.*)/s);

  if (!match) {
    return <span className="whitespace-pre-wrap">{text}</span>;
  }

  const [, handle, rest] = match;
  return (
    <span className="whitespace-pre-wrap">
      <span className="text-brand-400">{handle}</span>
      {rest}
    </span>
  );
}

/**
 * Renders the message text content with appropriate formatting based on message type.
 */
function MessageTextContent({
  message,
  sanitizedText,
  className,
  messageHasSources,
  hasShellFiles,
  sourcesMap,
  allSourcesMap,
  enableSourcesRendering,
  enableDataExplorerSources,
}: {
  message: UIMessageWithMetadata;
  sanitizedText: string;
  className?: string;
  messageHasSources: boolean;
  hasShellFiles: boolean;
  sourcesMap: SourcesMap;
  allSourcesMap?: SourcesMap;
  enableSourcesRendering?: boolean;
  enableDataExplorerSources?: boolean;
}) {
  const hasSandboxLinks = containsSandboxLinks(sanitizedText);

  // Assistant messages with sources or shell files get citations/sandbox link rendering
  if (
    (messageHasSources || hasShellFiles || hasSandboxLinks) &&
    message.role === 'assistant' &&
    enableSourcesRendering !== false
  ) {
    return (
      <MarkdownWithCitations
        messageUuid={message.id}
        sourcesMap={sourcesMap}
        allSourcesMap={allSourcesMap}
        enableDataExplorerSources={enableDataExplorerSources}
        className={cn('size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0', className)}
      >
        {sanitizedText}
      </MarkdownWithCitations>
    );
  }

  // User messages with slash-prefixed Nova commands get highlighted handles
  if (message.role === 'user' && startsWithSkillCommand(sanitizedText)) {
    return <SlashCommandHighlightedText text={sanitizedText} />;
  }

  // Default: render as markdown
  return (
    <MessageResponse messageUuid={message.id} className={className}>
      {sanitizedText}
    </MessageResponse>
  );
}

function ChatFileListItem({ file, messageUuid }: { file: { uuid: string; filename: string }; messageUuid: string }) {
  const { preview, isDownloading } = useDownloadFile({});
  const analytics = useAnalytics();

  const handleClick = () => {
    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_DOWNLOADED, {
      fileExtension: getFileExtension(file.filename),
      fileUuid: file.uuid,
      messageUuid,
      source: 'message_attachment_preview',
    });
    void preview(file.uuid, file.filename);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'flex w-fit max-w-xs cursor-pointer items-center gap-2 rounded-sm px-1 py-1',
        'hover:bg-muted/50 transition-colors',
        { 'pointer-events-none opacity-50': isDownloading },
      )}
    >
      <FileIcon filename={file.filename} hasBackground={false} />
      <span className="text-steel-400 truncate text-xs">{file.filename}</span>
    </div>
  );
}

export function PreviewMessage({
  message,
  isStreaming,
  isLast,
  onReaction,
  sourceFallbackMap,
  toolRenderers,
  dataPartRenderers,
  beforeContentRenderer,
  beforeContentClassName,
  messageContentClassName,
  enableSourcesRendering,
}: PreviewMessageProps) {
  const chatStatus = useChatStatus();
  const notifications = useNotifications();
  const analytics = useAnalytics();
  const [updateMessageReaction] = useMutation(ADD_MESSAGE_REACTION, {});
  const overlayContext = useChatOverlayContextSafe();
  const isPublicMode = overlayContext?.isPublicMode ?? false;
  const isReadOnly = overlayContext?.isReadOnly ?? false;
  const isReasoningEnabled = useFeatureFlag('nova-display-reasoning', false);
  const isDataExplorerSourcesEnabled = useFeatureFlag('nova-data-explorer-sources', true);
  const clarificationPart = getClarificationPart(message);
  const messageHasBlockingClarification =
    isClarificationPending(message) ||
    (isLast && clarificationPart?.state === 'output-available' && chatStatus !== 'ready');

  const { messageHasSources, sourcesMap, sourcesArray, allSourcesMap, shellGeneratedFiles, hasShellFiles } =
    useMemo(() => {
      const currentMessageHasSources = hasSources(message, sourceFallbackMap);
      const currentSourcesMap = currentMessageHasSources
        ? extractSourcesFromMessage(message, sourceFallbackMap)
        : new Map();

      // All sources (unfiltered) — used by CoT and sandbox link resolution.
      const currentAllSourcesMap = extractAllSourcesFromMessage(message);
      const currentShellGeneratedFiles = Array.from(buildSandboxFileMap(currentAllSourcesMap).values()).filter(
        (file) => file.kind === 'file',
      );

      return {
        messageHasSources: currentMessageHasSources,
        sourcesMap: currentSourcesMap,
        sourcesArray: currentSourcesMap.size > 0 ? getSourcesArray(currentSourcesMap) : [],
        allSourcesMap: currentAllSourcesMap,
        shellGeneratedFiles: currentShellGeneratedFiles,
        hasShellFiles: currentShellGeneratedFiles.length > 0,
      };
    }, [message, sourceFallbackMap]);

  // Extract chain-of-thought steps from message parts (reasoning + data-progress)
  const messageIsWorking = message.role === 'assistant' && isLast && ['submitted', 'streaming'].includes(chatStatus);
  const { chainOfThoughtMeta, isWorking, stepProgressMap } = useChainOfThoughtSteps(
    message,
    messageIsWorking,
    isReasoningEnabled,
  );

  const handleReaction = (newReaction: MessageReaction) => {
    if (isReadOnly) {
      return;
    }

    // Toggle reaction - if same reaction clicked, remove it
    const reaction = message.metadata?.reaction === newReaction ? null : newReaction;
    if (!isPublicMode) {
      updateMessageReaction({
        variables: {
          messageUuid: message.id,
          reaction,
        },
        optimisticResponse: {
          updateMessageReaction: {
            uuid: message.id,
            reaction,
          },
        },
      });
    }

    onReaction(reaction);

    if (reaction) {
      notifications.add({
        type: 'success',
        duration: 2000,
        title: 'Thank you for your feedback!',
      });
    }
  };

  const [_, copyToClipboard] = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isCopied]);

  const handleCopyToClipboard = useCallback(() => {
    const lastMessagePart = message.parts?.[message.parts.length - 1];

    if (!lastMessagePart) {
      return;
    }

    copyToClipboard(lastMessagePart.type === 'text' ? lastMessagePart.text : '');
    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.ASSISTANT_MESSAGE_COPY_BUTTON_CLICKED, {
      hasGeneratedFiles: hasShellFiles,
      hasSources: messageHasSources,
      messageLengthBucket: lastMessagePart.type === 'text' ? getLengthBucket(lastMessagePart.text.length) : undefined,
      messageRole: message.role,
      messageUuid: message.id,
    });
    setIsCopied(true);
  }, [analytics, copyToClipboard, hasShellFiles, message.id, message.parts, message.role, messageHasSources]);

  const handleCopySelection = useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      if (message.role !== 'assistant') {
        return;
      }

      const selection = window.getSelection();
      const selectionText = selection?.toString() ?? '';
      if (!selectionText.trim()) {
        return;
      }

      const anchorNode = selection?.anchorNode;
      const focusNode = selection?.focusNode;
      const target = event.currentTarget;
      const selectionInsideMessage =
        (!anchorNode || target.contains(anchorNode)) && (!focusNode || target.contains(focusNode));

      if (!selectionInsideMessage) {
        return;
      }

      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_SELECTION_COPIED, {
        hasGeneratedFiles: hasShellFiles,
        hasSources: messageHasSources,
        messageRole: message.role,
        messageUuid: message.id,
        selectionLengthBucket: getLengthBucket(selectionText.length),
      });
    },
    [analytics, hasShellFiles, message.id, message.role, messageHasSources],
  );

  // Skip rendering the wrapper for assistant messages with no visible content
  // (e.g. streaming just started, only step-start parts exist).
  // This prevents an empty div from occupying space above the thinking dots.
  // NOTE: placed after all hooks to respect React's rules of hooks.
  const hasRenderableContent =
    message.role === 'user' ||
    chainOfThoughtMeta.hasTextPart ||
    (isReasoningEnabled && chainOfThoughtMeta.steps.length > 0) ||
    (message.metadata?.files?.length ?? 0) > 0 ||
    message.parts?.some(
      (p) =>
        (p.type.startsWith('tool-') && toolRenderers?.[p.type.replace('tool-', '')]) ||
        (p.type.startsWith('data-') && dataPartRenderers?.[p.type.replace('data-', '')]),
    );

  if (!hasRenderableContent) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        data-testid={`message-${message.role}`}
        className="group/message mx-auto w-full max-w-[800px] sm:px-8"
        initial={{ y: 5, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        data-role={message.role}
        key={message.id}
        onCopy={handleCopySelection}
      >
        <div
          className={cn(
            'flex w-full items-start gap-4 group-data-[role=user]/message:ml-auto group-data-[role=user]/message:max-w-full',
          )}
        >
          <div className="flex w-full flex-col gap-2">
            {message.metadata?.files && message.metadata.files.length > 0 && (
              <div className="flex flex-row flex-wrap gap-2">
                {message.metadata?.files?.map((file) => (
                  <ChatFileListItem key={file.uuid} file={file} messageUuid={message.id} />
                ))}
              </div>
            )}
            <div className={cn({ 'ml-auto max-w-full': message.role === 'user' })}>
              {(() => {
                let nextCoTStepIndex = 0;
                const lastCoTStepIndex = chainOfThoughtMeta.steps.at(-1)?.stepIndex;
                const textPartIndexes = message.parts
                  ?.map((part, index) => (part.type === 'text' ? index : null))
                  .filter((index): index is number => index !== null);
                const finalAnswerTextPartIndex = message.parts?.findIndex(isFinalAnswerTextPart);
                const hasFinalAnswerTextPart = finalAnswerTextPartIndex != null && finalAnswerTextPartIndex >= 0;
                const finalTextPartIndex = hasFinalAnswerTextPart ? finalAnswerTextPartIndex : textPartIndexes?.at(-1);
                const shouldCollapseCompletedTrace =
                  isReasoningEnabled &&
                  message.role === 'assistant' &&
                  (!isWorking || hasFinalAnswerTextPart) &&
                  !isClarificationPending(message) &&
                  chainOfThoughtMeta.steps.length > 0;

                const getChainOfThoughtBoundaryPartIndex = (
                  part: NonNullable<typeof message.parts>[number],
                  index: number,
                ) => {
                  if (part.type === 'reasoning') {
                    return null;
                  }

                  if (part.type.startsWith('tool-')) {
                    const toolName = part.type.replace('tool-', '');

                    if (toolName === 'clarification') {
                      return index + 1;
                    }

                    const toolWillRender =
                      toolRenderers?.[toolName] &&
                      !(isReasoningEnabled && 'state' in part && part.state !== 'output-available');

                    return toolWillRender ? index + 1 : null;
                  }

                  if (part.type.startsWith('data-')) {
                    if (part.type === 'data-progress' && isReasoningEnabled) {
                      return null;
                    }

                    const dataPartType = part.type.replace('data-', '');
                    return dataPartRenderers?.[dataPartType] ? index : null;
                  }

                  return part.type === 'text' ? index : null;
                };

                const renderChainOfThoughtBeforePart = (
                  partIndex: number,
                  key: string,
                  {
                    className,
                    groupingMode = 'interleaved',
                  }: { className?: string; groupingMode?: CoTStepBlockGroupingMode } = {},
                ) => {
                  if (!isReasoningEnabled) return null;

                  const segment = getCoTStepSegmentBeforePart(chainOfThoughtMeta.steps, nextCoTStepIndex, partIndex);

                  if (segment.length === 0) return null;

                  nextCoTStepIndex = (segment.at(-1)?.stepIndex ?? nextCoTStepIndex) + 1;
                  const segmentIsWorking = isWorking && segment.at(-1)?.stepIndex === lastCoTStepIndex;

                  const stepBlocks = getCoTStepBlocks(segment);
                  const renderStepBlock = (
                    steps: (typeof stepBlocks)[number],
                    blockIndex: number | string,
                    blockClassName = className,
                  ) => {
                    const shouldSkipStaticClarificationBlock =
                      steps.length === 1 && steps[0]?.type === 'tool' && steps[0].toolName === 'clarification';

                    if (shouldSkipStaticClarificationBlock) {
                      return null;
                    }

                    const blockLastStepIndex = steps.at(-1)?.stepIndex;
                    return (
                      <ChainOfThoughtBlock
                        key={`${key}-${blockIndex}`}
                        steps={steps}
                        allSteps={chainOfThoughtMeta.steps}
                        isWorking={segmentIsWorking && blockLastStepIndex === lastCoTStepIndex}
                        stepProgressMap={stepProgressMap}
                        sourcesMap={isDataExplorerSourcesEnabled ? allSourcesMap : undefined}
                        className={blockClassName}
                      />
                    );
                  };

                  return getCoTStepBlockRenderItems(stepBlocks, {
                    activeStepIndex: segmentIsWorking ? lastCoTStepIndex : undefined,
                    mode: groupingMode,
                  }).map((item, itemIndex) => {
                    if (item.type === 'block') {
                      return item.blocks.map((steps, blockIndex) =>
                        renderStepBlock(steps, `${itemIndex}-${blockIndex}`),
                      );
                    }

                    return (
                      <GroupedChainOfThoughtBlock
                        key={`${key}-group-${itemIndex}`}
                        itemCount={item.blocks.length}
                        icon={item.icon}
                        title={item.title}
                        className={className}
                      >
                        {item.blocks.map((steps, blockIndex) =>
                          renderStepBlock(steps, `${itemIndex}-${blockIndex}`, 'mb-0'),
                        )}
                      </GroupedChainOfThoughtBlock>
                    );
                  });
                };

                const renderPart = (
                  part: NonNullable<typeof message.parts>[number],
                  index: number,
                  { mutedText = false }: { mutedText?: boolean } = {},
                ) => {
                  const { type } = part;
                  const key = `message-${message.id}-part-${index}`;
                  let renderedPart: ReactNode = null;

                  if (type.startsWith('tool-')) {
                    const toolName = type.replace('tool-', '');

                    // When reasoning is enabled, tool pending states are already
                    // displayed inside the ChainOfThought block — skip them here.
                    if (
                      !(
                        isReasoningEnabled &&
                        toolName !== 'clarification' &&
                        'state' in part &&
                        part.state !== 'output-available'
                      ) &&
                      toolRenderers?.[toolName]
                    ) {
                      const rendered = toolRenderers[toolName](part, message.id);
                      if (rendered) {
                        const isResolvedClarification =
                          isClarificationToolPart(part) && part.state === 'output-available';

                        renderedPart = (
                          <div className={cn({ 'flex w-full justify-end': isResolvedClarification })}>{rendered}</div>
                        );
                      }
                    }
                  } else if (type.startsWith('data-')) {
                    const dataPartType = type.replace('data-', '');
                    const DataPartComponent = dataPartRenderers?.[dataPartType];
                    if (!(type === 'data-progress' && isReasoningEnabled) && DataPartComponent) {
                      renderedPart = <DataPartComponent />;
                    }
                  } else if (type === 'text') {
                    const sanitizedText = sanitizeText(part.text);

                    renderedPart = (
                      <div className="flex flex-row items-start gap-2">
                        <div
                          data-testid="message-content"
                          className={cn(
                            'flex max-w-full flex-col gap-4 rounded-[20px]',
                            mutedText ? 'py-1 leading-6' : 'py-2',
                            {
                              'bg-muted rounded-br-[0px]': message.role === 'user',
                              'rounded-bl-[0px]': message.role === 'assistant',
                              'px-5': message.role === 'user',
                            },
                            messageContentClassName,
                          )}
                        >
                          {beforeContentRenderer && message.role === 'user' && (
                            <div className={beforeContentClassName}>{beforeContentRenderer(message)}</div>
                          )}
                          <MessageTextContent
                            message={message}
                            sanitizedText={sanitizedText}
                            className={mutedText ? 'leading-6' : undefined}
                            messageHasSources={messageHasSources}
                            hasShellFiles={hasShellFiles}
                            sourcesMap={sourcesMap}
                            allSourcesMap={allSourcesMap}
                            enableSourcesRendering={enableSourcesRendering}
                            enableDataExplorerSources={isDataExplorerSourcesEnabled}
                          />
                        </div>
                      </div>
                    );
                  }

                  if (!renderedPart) return null;

                  return <Fragment key={key}>{renderedPart}</Fragment>;
                };

                if (shouldCollapseCompletedTrace) {
                  nextCoTStepIndex = 0;
                  const trailNodes: ReactNode[] = [];
                  const visibleResponseNodes: ReactNode[] = [];

                  message.parts?.forEach((part, index) => {
                    const key = `message-${message.id}-part-${index}`;
                    const chainOfThoughtBoundaryPartIndex = getChainOfThoughtBoundaryPartIndex(part, index);
                    const isSkillOfferPart = part.type === 'data-skillOffer';
                    const shouldUseStandaloneGrouping =
                      trailNodes.length === 0 &&
                      ((part.type === 'text' && index === finalTextPartIndex) || isSkillOfferPart);

                    if (chainOfThoughtBoundaryPartIndex != null) {
                      const chainOfThoughtNode = renderChainOfThoughtBeforePart(
                        chainOfThoughtBoundaryPartIndex,
                        `${key}-cot`,
                        {
                          className: 'mb-0',
                          groupingMode: shouldUseStandaloneGrouping ? 'standalone' : 'interleaved',
                        },
                      );
                      if (chainOfThoughtNode) {
                        trailNodes.push(chainOfThoughtNode);
                      }
                    }

                    const renderedPart = renderPart(part, index, {
                      mutedText: part.type === 'text' && index !== finalTextPartIndex,
                    });
                    if (!renderedPart) return;

                    const isPendingClarificationPart =
                      isClarificationToolPart(part) && part.state !== 'output-available';

                    if (
                      (part.type === 'text' && index === finalTextPartIndex) ||
                      isPendingClarificationPart ||
                      isSkillOfferPart
                    ) {
                      visibleResponseNodes.push(renderedPart);
                    } else {
                      trailNodes.push(renderedPart);
                    }
                  });

                  const tailNode = renderChainOfThoughtBeforePart(
                    Number.POSITIVE_INFINITY,
                    `message-${message.id}-cot-tail`,
                    { className: 'mb-0', groupingMode: trailNodes.length === 0 ? 'standalone' : 'interleaved' },
                  );
                  if (tailNode) {
                    trailNodes.push(tailNode);
                  }

                  return (
                    <>
                      {trailNodes.length > 0 && (
                        <CompletedChainOfThoughtBlock
                          itemCount={trailNodes.length}
                          allSteps={chainOfThoughtMeta.steps}
                          title="How Nova got here"
                        >
                          {trailNodes}
                        </CompletedChainOfThoughtBlock>
                      )}
                      {visibleResponseNodes}
                    </>
                  );
                }

                const renderedParts = message.parts?.map((part, index) => {
                  const key = `message-${message.id}-part-group-${index}`;
                  const chainOfThoughtBoundaryPartIndex = getChainOfThoughtBoundaryPartIndex(part, index);

                  return (
                    <Fragment key={key}>
                      {chainOfThoughtBoundaryPartIndex != null &&
                        renderChainOfThoughtBeforePart(chainOfThoughtBoundaryPartIndex, `${key}-cot`)}
                      {renderPart(part, index)}
                    </Fragment>
                  );
                });

                return (
                  <>
                    {renderedParts}
                    {renderChainOfThoughtBeforePart(Number.POSITIVE_INFINITY, `message-${message.id}-cot-tail`)}
                  </>
                );
              })()}
            </div>

            {message.role === 'assistant' && hasShellFiles && (
              <GeneratedFilesList
                files={shellGeneratedFiles.map((file) => ({
                  uuid: file.fileUuid,
                  filename: file.filename,
                  isPending: file.isPending,
                }))}
                messageUuid={message.id}
                source="shell_generated_file"
              />
            )}

            {/* Show sources panel for assistant messages with sources */}
            {message.role === 'assistant' && sourcesArray.length > 0 && enableSourcesRendering !== false && (
              <Sources>
                <SourcesTrigger count={sourcesArray.length} />
                <SourcesContent>
                  <div className="flex flex-wrap gap-2">
                    {sourcesArray.map((source, index) => (
                      <SourceBadge key={source.sourceId} source={source} index={index} />
                    ))}
                  </div>
                </SourcesContent>
              </Sources>
            )}

            {/* Show reactions only for assistant messages */}
            {message.role === 'assistant' && !(isStreaming && isLast) && !messageHasBlockingClarification && (
              <div className="transition-opacity">
                <MessageActions>
                  <MessageAction
                    tooltip={isCopied ? 'Copied!' : 'Copy to clipboard'}
                    label={isCopied ? 'Copied!' : 'Copy to clipboard'}
                    onClick={handleCopyToClipboard}
                  >
                    {isCopied ? (
                      <Check aria-label="Copied" color="currentColor" size={18} />
                    ) : (
                      <ClipboardList aria-label="Copy to clipboard" color="currentColor" size={18} />
                    )}
                  </MessageAction>
                  {!isReadOnly && (message.metadata?.reaction === 'THUMBS_UP' || !message.metadata?.reaction) && (
                    <MessageAction
                      tooltip="Good response"
                      label="Thumbs up"
                      isActive={message.metadata?.reaction === 'THUMBS_UP'}
                      onClick={() => handleReaction('THUMBS_UP')}
                    >
                      <ThumbsUp aria-label="Thumbs up" active={message.metadata?.reaction === 'THUMBS_UP'} />
                    </MessageAction>
                  )}
                  {!isReadOnly && (message.metadata?.reaction === 'THUMBS_DOWN' || !message.metadata?.reaction) && (
                    <MessageAction
                      tooltip="Bad response"
                      label="Thumbs down"
                      isActive={message.metadata?.reaction === 'THUMBS_DOWN'}
                      aria-label="Thumbs down"
                      onClick={() => handleReaction('THUMBS_DOWN')}
                    >
                      <ThumbsDown aria-label="Thumbs down" active={message.metadata?.reaction === 'THUMBS_DOWN'} />
                    </MessageAction>
                  )}
                  <ChatContextUsage messageId={message.id} />
                </MessageActions>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
